import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "@/components/common/BackButton";
import { useToast } from "@/components/ui/use-toast";
import { BillingPageState } from "../billing-settings/components/BillingPageState";
import {
  CustomPageForm,
  type CustomPageFormValues,
} from "./components/CustomPageForm";
import { useCustomPage, useUpdateCustomPage } from "./hooks/useCustomPages";
import { getEmptyRichEditorData } from "@/features/rich-editor";
import { buildContentWithFreshImages } from "@/features/rich-editor/rich-editor.utils";
import { fileService } from "@/services/file-service";
import type { CustomPageContentData } from "@/types/custom-page";

const resolveImageUrl = (fileId: string) => fileService.getSignedUrl(fileId);

export const EditCustomPagePage: React.FC = () => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { toast } = useToast();
  const { data: customPage, isLoading, isError } = useCustomPage(id);
  const updateCustomPage = useUpdateCustomPage();
  // Saved image URLs are signed and expire; re-sign them by fileId once per
  // page id before the editor is shown. Later refetches do not touch the form.
  const [refreshed, setRefreshed] = useState<{
    id: string;
    values: CustomPageFormValues;
  } | null>(null);
  const customPageRef = useRef(customPage);
  customPageRef.current = customPage;
  const loadedId = customPage?.id;

  useEffect(() => {
    const page = customPageRef.current;

    if (!page || page.id !== loadedId) {
      return;
    }

    let cancelled = false;
    const stored = page.contentData ?? null;
    const base = {
      title: page.title,
      buttonTitle: page.buttonTitle,
      enabled: page.enabled,
    };

    const done = (
      contentData: CustomPageContentData | null,
      content: string,
    ) => {
      if (!cancelled) {
        setRefreshed({
          id: page.id,
          values: {
            ...base,
            content,
            contentData: contentData ?? getEmptyRichEditorData(),
          },
        });
      }
    };

    if (!stored) {
      done(null, page.content);
      return;
    }

    buildContentWithFreshImages(stored, page.content, resolveImageUrl)
      .then((r) => done(r.contentData, r.content))
      .catch(() => done(stored, page.content));

    return () => {
      cancelled = true;
    };
  }, [loadedId]);

  const isRefreshing = refreshed?.id !== id;
  const defaultValues = refreshed?.id === id ? refreshed.values : undefined;

  const handleSubmit = async (data: CustomPageFormValues) => {
    // Links may have expired while the form was open: re-sign before saving.
    let payload: CustomPageFormValues;

    try {
      const result = await buildContentWithFreshImages(
        data.contentData,
        data.content,
        resolveImageUrl,
      );

      payload = {
        ...data,
        contentData: result.contentData,
        content: result.content,
      };

      if (result.failed > 0) {
        toast({
          title: "Warning",
          description: "Some images could not be refreshed.",
          variant: "destructive",
        });
      }
    } catch {
      payload = data;
    }

    await updateCustomPage.mutateAsync({
      id,
      data: payload,
    });
    toast({
      title: "Success",
      description: "Custom page updated successfully",
    });
    navigate("/custom-pages");
  };

  if (isLoading || isRefreshing) {
    return <BillingPageState text="Loading custom page..." />;
  }

  if (isError || !customPage) {
    return <BillingPageState text="Failed to load custom page" tone="error" />;
  }

  return (
    <div className="flex w-full flex-col space-y-8 p-12">
      <BackButton className="self-start" />

      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-zinc-900">
          Edit Custom Page
        </h1>
        <p className="text-zinc-500">
          Update the guest-facing HTML content and availability.
        </p>
      </div>

      <CustomPageForm
        defaultValues={defaultValues}
        submitLabel="Save changes"
        isSubmitting={updateCustomPage.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
